/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import moment from 'moment-timezone';
import { INCLUDE_FIRST_COME_FIRST_SERVED } from '../constants';

export function getFacilityMaxTotalGuests(facilityPurchaseLimits, accountType) {
    // determine maxTotalGuests from external account type and facility purchase limits
    let maxTotalGuests = 0; // zero represents that there is no max total guest limit
    if (facilityPurchaseLimits) {
        const purchaseLimitForThisAccountType =
            facilityPurchaseLimits[accountType] || facilityPurchaseLimits.ANY;
        if (purchaseLimitForThisAccountType) {
            maxTotalGuests = purchaseLimitForThisAccountType;
        }
    }

    return maxTotalGuests;
}

export function getTourMaxTotalGuests(tourPurchaseMaximums, accountType) {
    // determine minTotalGuests from external account type and tour purchase minimums
    let maxTotalGuests = 0; // zero represents that there is no max total guest limit
    if (tourPurchaseMaximums) {
        const purchaseMaximumForThisAccountType =
            tourPurchaseMaximums[accountType] || tourPurchaseMaximums.ANY;
        if (purchaseMaximumForThisAccountType) {
            maxTotalGuests = purchaseMaximumForThisAccountType;
        }
    }

    return maxTotalGuests;
}

/**
 * @param facility the current facility
 * @param accountType the user account type, i.e. FIT or COMM
 * @returns an integer representing the minimum number of guests required
 * to create a reservation for a given tour or facility.
 *
 * NOTES: the facility purchase maximums are the default and can be
 * overridden at the tour level. Neither is required. If there is a
 * maximum set at the facility level, then all tours will inherit this
 * maximum. If any single tour has a minimum set, this will override the
 * facility-level minimum (if there is one)
 */
export function getMaxTotalGuests({ facility, tour, accountType = 'ANY' }) {
    let maxTotalGuests = 0;

    if (
        tour?.purchase_maximums &&
        (tour.purchase_maximums[accountType] || tour.purchase_maximums.ANY)
    ) {
        maxTotalGuests = getTourMaxTotalGuests(tour.purchase_maximums, accountType);
    } else if (
        facility?.purchase_maximums &&
        (facility.purchase_maximums[accountType] || facility.purchase_maximums.ANY)
    ) {
        maxTotalGuests = getFacilityMaxTotalGuests(
            facility.purchase_maximums,
            accountType
        );
    } else if (
        facility?.purchase_limits &&
        (facility.purchase_limits[accountType] || facility.purchase_limits.ANY)
    ) {
        maxTotalGuests = getFacilityMaxTotalGuests(facility.purchase_limits, accountType);
    }
    return maxTotalGuests;
}

/**
 * Helper function to generate initial GuestCounter value state
 */
export function getInitialCounts(
    allowedGuestTypeIds,
    guestCountFromFilters,
    maxTotalGuests,
    minTotalGuestsByGuestType
) {
    const initialCounts = {};

    if (allowedGuestTypeIds) {
        allowedGuestTypeIds.forEach((guestTypeId, index) => {
            // If there's only 1 type and the max is 1, we set the value for the user
            if (index === 0 && maxTotalGuests && allowedGuestTypeIds.length === 1) {
                initialCounts[guestTypeId] = 1;
            }
            // If the user set the guest count on the facility page
            else if (index === 0 && !!guestCountFromFilters) {
                initialCounts[guestTypeId] = guestCountFromFilters;
            } else if (
                minTotalGuestsByGuestType &&
                minTotalGuestsByGuestType[guestTypeId]
            ) {
                initialCounts[guestTypeId] = minTotalGuestsByGuestType[guestTypeId];
            }
            // Otherwise the initial value is 0
            else {
                initialCounts[guestTypeId] = 0;
            }
        });
    }

    return initialCounts;
}

export function getTimeOptions({
    tourTimes,
    countTotal = 1,
    timezone = 'America/New_York',
    calculateBookingWindowClose = true,
}) {
    const timeOptions = [];
    let firstReleasedDate = null;
    let secondReleasedDate = null;
    let moreAvailableDate = null;
    let additionalMoreAvailableDate = null;
    let hasAvailability = false;
    let hasWalkupAvailability = false;

    if (!tourTimes || !tourTimes.length) {
        return {
            timeOptions,
            firstReleasedDate,
            secondReleasedDate,
            moreAvailableDate,
            additionalMoreAvailableDate,
            hasAvailability,
            hasWalkupAvailability,
        };
    }

    for (let i = 0; i < tourTimes.length; i += 1) {
        const item = tourTimes[i];
        let firstReleasedItemMoment = null;
        let secondReleasedItemMoment = null;
        let currentItemMoment = null;
        let secondaryItemMoment = null;
        const nowMoment = moment().tz(timezone);
        const isBookingWindowPast = nowMoment.isAfter(item.bookingCloseTime);

        const displayTime = calculateBookingWindowClose
            ? item.displayDuration
            : item.displayTime;
        const timeOptionDefaultValues = {
            displayDuration: displayTime,
            availableCount: item.availableCount,
            unreleasedCount: item?.unreleasedCount,
            availableWalkupCount: item.availableWalkupCount,
            value: item.time,
        };

        // Make moreAvailableDate data --> the earliest moreAvailableReleaseTime
        if (
            !item.isAvailable &&
            (item.moreAvailableReleaseTime || item.secondaryMoreAvailableReleaseTime)
        ) {
            currentItemMoment = moment(item.moreAvailableReleaseTime);
            secondaryItemMoment = moment(item.secondaryMoreAvailableReleaseTime);

            if (timezone) {
                currentItemMoment.tz(timezone);
                secondaryItemMoment.tz(timezone);
            }

            if (!moreAvailableDate) {
                moreAvailableDate = currentItemMoment || secondaryItemMoment;
            }

            if (!additionalMoreAvailableDate && moreAvailableDate) {
                if (currentItemMoment.isAfter(moreAvailableDate)) {
                    additionalMoreAvailableDate = currentItemMoment;
                }

                if (secondaryItemMoment.isAfter(moreAvailableDate)) {
                    additionalMoreAvailableDate = secondaryItemMoment;
                }
            }

            if (moreAvailableDate && currentItemMoment.isBefore(moreAvailableDate)) {
                additionalMoreAvailableDate = moreAvailableDate;
                moreAvailableDate = currentItemMoment;
            }

            if (moreAvailableDate && secondaryItemMoment.isBefore(moreAvailableDate)) {
                additionalMoreAvailableDate = moreAvailableDate;
                moreAvailableDate = secondaryItemMoment;
            }
        }

        firstReleasedItemMoment =
            item?.firstReleasedTime && moment(item.firstReleasedTime);
        secondReleasedItemMoment =
            item?.secondReleasedTime && moment(item.secondReleasedTime);

        if (firstReleasedItemMoment) {
            if (timezone) {
                firstReleasedItemMoment.tz(timezone);
            }

            if (
                !firstReleasedDate ||
                firstReleasedItemMoment?.isBefore(firstReleasedDate)
            ) {
                firstReleasedDate = firstReleasedItemMoment?.clone();
                firstReleasedItemMoment = null;
            }
        }

        if (
            firstReleasedDate &&
            firstReleasedItemMoment &&
            !firstReleasedItemMoment?.isSame(firstReleasedDate) &&
            (!secondReleasedDate || firstReleasedItemMoment?.isBefore(secondReleasedDate))
        ) {
            secondReleasedDate = firstReleasedItemMoment;
        }

        if (secondReleasedItemMoment) {
            if (timezone) {
                secondReleasedItemMoment.tz(timezone);
            }

            if (
                !secondReleasedDate ||
                secondReleasedItemMoment?.isBefore(secondReleasedDate)
            ) {
                secondReleasedDate = secondReleasedItemMoment;
            }
        }

        // Make timeOptions array
        // -----------------------

        // After booking window
        if (isBookingWindowPast) {
            timeOptions.push({
                ...timeOptionDefaultValues,
                isDisabled: true,
                tooltip: `Past cutoff time`,
            });
        }
        // If available and the inventory is greater than requested amount
        else if (item.isAvailable && countTotal <= item.availableCount) {
            hasAvailability = true;

            timeOptions.push({
                ...timeOptionDefaultValues,
            });
        }
        // If available but requested amount is greater than inventory
        else if (item.isAvailable && countTotal > item.availableCount) {
            hasAvailability = true;

            timeOptions.push({
                ...timeOptionDefaultValues,
                isDisabled: true,
                tooltip: `Only ${item.availableCount} available`,
            });
        }
        // If not available but there's a future release
        else if (!item.isAvailable && currentItemMoment) {
            timeOptions.push({
                ...timeOptionDefaultValues,
                isDisabled: true,
                tooltip: `More will be made available on ${currentItemMoment.format(
                    'MMM. D'
                )} at ${currentItemMoment.format('hh:mm a z')}`,
                hideAvailableCount: true,
            });
        }
        // If not available but there's walkup inventory
        else if (
            !item.isAvailable &&
            !item.unreleasedCount &&
            item?.availableWalkupCount > 0
        ) {
            hasWalkupAvailability = true;
            timeOptions.push({
                ...timeOptionDefaultValues,
                isDisabled: true,
                tooltip: `Sold out. First-come, first-served tickets may be available once you are physically on site at this location, but there is no guarantee.`,
            });
        }
        // If not available but there's walkup inventory
        else if (!item.isAvailable) {
            timeOptions.push({
                ...timeOptionDefaultValues,
                isDisabled: true,
                tooltip: `Sold out`,
            });
        }
    }

    // We show additional tickets date
    if (firstReleasedDate) {
        additionalMoreAvailableDate = moreAvailableDate?.clone();
        moreAvailableDate = null;
    }

    return {
        timeOptions,
        firstReleasedDate,
        secondReleasedDate,
        moreAvailableDate,
        additionalMoreAvailableDate,
        hasAvailability,
        hasWalkupAvailability,
    };
}

export const includeFirstComeFirstServed = (facilityId) => {
    if (facilityId) {
        return INCLUDE_FIRST_COME_FIRST_SERVED?.some((id) => facilityId === id);
    }

    return false;
};
